import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import carIcon from "../../../assets/imgs/lobby/map/map_pin_ks_white.svg";
import {
  BasePoint,
  CoursePoint,
  BpsLogPoint,
  MapTileLayer,
} from "../utils/types";
import type { MapActualBitrateColoring } from "../utils/settings";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

type Props = {
  basePoint: BasePoint | null;
  baseName: string;
  coursePoints: CoursePoint[];
  bpslogData: BpsLogPoint[];
  pointerPoint: BpsLogPoint | null;
  followPointer: boolean;
  mapTileLayer: MapTileLayer;
  mapActualBitrateColoring: MapActualBitrateColoring;
  vehicleId: number | null;
  isRealtime: boolean;
  startInput: string;
  endInput: string;
  realtimeWindowMinutes: number;
  visualizationMode?: "points" | "heatmap";
  heatmapCellMeters?: number;
  heatmapBaseOpacity?: number;
  heatmapRenderMode?: "grid" | "smooth";
  heatmapGridScoreColorMid?: number;
  heatmapGridScoreColorLow?: number;
  heatmapSmoothScoreColorMid?: number;
  heatmapSmoothScoreColorLow?: number;
  onPickInfoChange?: (info: BpsLogMapPickInfo | null) => void;
  onRenderBusyChange?: (busy: boolean) => void;
  renderCancelToken?: number;
};

export type BpsLogMapPickInfo = {
  centerLat: number;
  centerLng: number;
  count: number;
  pickedPoint: BpsLogPoint | null;
  points: BpsLogPoint[];
  stats: {
    minActualMbps: number | null;
    p10ActualMbps: number | null;
    maxActualMbps: number | null;
    avgActualMbps: number | null;
    lowCount: number;
    lowRate: number;
    weaknessScore: number;
  };
};

const hasGpsPoint = (
  pt: BpsLogPoint,
): pt is BpsLogPoint & { gps: { lat: number; lng: number } } => {
  return pt?.gps?.lat != null && pt?.gps?.lng != null;
};

const BpsLogMap = ({
  basePoint,
  baseName,
  coursePoints,
  bpslogData,
  pointerPoint,
  followPointer,
  mapTileLayer,
  mapActualBitrateColoring,
  vehicleId,
  isRealtime,
  startInput,
  endInput,
  realtimeWindowMinutes,
  visualizationMode,
  heatmapCellMeters,
  heatmapBaseOpacity,
  heatmapRenderMode,
  heatmapGridScoreColorMid,
  heatmapGridScoreColorLow,
  heatmapSmoothScoreColorMid,
  heatmapSmoothScoreColorLow,
  onPickInfoChange,
  onRenderBusyChange,
  renderCancelToken,
}: Props) => {
  const mode: "points" | "heatmap" =
    visualizationMode === "heatmap" ? "heatmap" : "points";

  const renderMode: "grid" | "smooth" =
    heatmapRenderMode === "smooth" ? "smooth" : "grid";
  const cellMeters =
    typeof heatmapCellMeters === "number" && Number.isFinite(heatmapCellMeters)
      ? Math.max(5, Math.min(500, heatmapCellMeters))
      : 30;

  const baseOpacity =
    typeof heatmapBaseOpacity === "number" &&
    Number.isFinite(heatmapBaseOpacity)
      ? Math.max(0, Math.min(0.95, heatmapBaseOpacity))
      : 0.08;

  const activeScoreColorMidRaw =
    renderMode === "smooth"
      ? heatmapSmoothScoreColorMid
      : heatmapGridScoreColorMid;
  const activeScoreColorLowRaw =
    renderMode === "smooth"
      ? heatmapSmoothScoreColorLow
      : heatmapGridScoreColorLow;

  const scoreColorMid =
    typeof activeScoreColorMidRaw === "number" &&
    Number.isFinite(activeScoreColorMidRaw)
      ? Math.max(0.01, Math.min(0.99, activeScoreColorMidRaw))
      : 0.5;
  const scoreColorLow =
    typeof activeScoreColorLowRaw === "number" &&
    Number.isFinite(activeScoreColorLowRaw)
      ? Math.max(scoreColorMid, Math.min(1, activeScoreColorLowRaw))
      : 0.9;

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const layersControlRef = useRef<L.Control.Layers | null>(null);
  const baseMarkerRef = useRef<L.Marker | null>(null);
  const vehicleMarkerRef = useRef<L.Marker | null>(null);
  const baseLayerRef = useRef<L.LayerGroup | null>(null);
  const vehicleLayerRef = useRef<L.LayerGroup | null>(null);
  const courseLayerRef = useRef<L.LayerGroup | null>(null);
  const trailLayerRef = useRef<L.LayerGroup | null>(null);
  const trailMarkersRef = useRef<L.CircleMarker[]>([]);
  const trailPointKeysRef = useRef<string[]>([]);
  const trailRendererRef = useRef<L.Renderer | null>(null);
  const heatLayerRef = useRef<L.LayerGroup | null>(null);
  const heatSelectionLayerRef = useRef<L.LayerGroup | null>(null);
  const heatRectsRef = useRef<Array<L.Rectangle>>([]);
  const selectedKeyRef = useRef<string | null>(null);
  const ignoreNextMapClickRef = useRef<boolean>(false);

  const renderCancelTokenRef = useRef<number>(0);
  useEffect(() => {
    renderCancelTokenRef.current =
      typeof renderCancelToken === "number" &&
      Number.isFinite(renderCancelToken)
        ? renderCancelToken
        : 0;
  }, [renderCancelToken]);

  const renderBusySeqRef = useRef(0);
  const markRenderBusy = (busy: boolean) => {
    onRenderBusyChange?.(busy);
  };

  // Leaflet地図の初期化とクリーンアップを行う。
  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapRef.current) return;

    const defaultIcon = L.icon({
      iconUrl: markerIcon,
      iconRetinaUrl: markerIcon2x,
      shadowUrl: markerShadow,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });

    const initialLatLng: L.LatLngExpression = [35, 140];
    const map = L.map(mapContainerRef.current, {
      zoomControl: true,
      attributionControl: true,
      maxZoom: 22,
    }).setView(initialLatLng, 14);

    mapRef.current = map;

    baseLayerRef.current = L.layerGroup().addTo(map);
    vehicleLayerRef.current = L.layerGroup().addTo(map);
    courseLayerRef.current = L.layerGroup().addTo(map);
    trailLayerRef.current = L.layerGroup().addTo(map);
    heatLayerRef.current = L.layerGroup().addTo(map);
    heatSelectionLayerRef.current = L.layerGroup().addTo(map);

    trailRendererRef.current = L.canvas({ padding: 0.5 });

    baseMarkerRef.current = L.marker(initialLatLng, {
      icon: defaultIcon,
    }).addTo(baseLayerRef.current);

    const vehicleIcon = L.icon({
      iconUrl: carIcon,
      iconSize: [75, 75],
      iconAnchor: [2, 53],
    });
    vehicleMarkerRef.current = L.marker(initialLatLng, {
      icon: vehicleIcon,
      zIndexOffset: 1000,
    }).addTo(vehicleLayerRef.current);

    layersControlRef.current = L.control.layers(
      undefined,
      {
        アイコン: vehicleLayerRef.current,
        軌跡: trailLayerRef.current,
        拠点: baseLayerRef.current,
        停留所: courseLayerRef.current,
      },
      { collapsed: true },
    );
    layersControlRef.current.addTo(map);

    requestAnimationFrame(() => {
      map.invalidateSize();
      requestAnimationFrame(() => map.invalidateSize());
    });

    return () => {
      layersControlRef.current?.remove();
      try {
        tileLayerRef.current?.remove();
      } catch (e) {
        // 無視
      }
      map.remove();
      mapRef.current = null;
      tileLayerRef.current = null;
      layersControlRef.current = null;
      baseMarkerRef.current = null;
      vehicleMarkerRef.current = null;
      baseLayerRef.current = null;
      vehicleLayerRef.current = null;
      courseLayerRef.current = null;
      trailLayerRef.current = null;
      trailMarkersRef.current = [];
      trailPointKeysRef.current = [];
      trailRendererRef.current = null;
      heatLayerRef.current = null;
      heatSelectionLayerRef.current = null;
      heatRectsRef.current = [];
      selectedKeyRef.current = null;
      ignoreNextMapClickRef.current = false;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // タイル種別に応じて背景タイルレイヤーを生成する。
    const buildTileLayer = (kind: MapTileLayer) => {
      if (kind === "dark") {
        return L.tileLayer(
          "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
          {
            attribution: "&copy; OpenStreetMap contributors &copy; CARTO",
            maxNativeZoom: 19,
            maxZoom: 22,
            subdomains: "abcd",
          },
        );
      }

      return L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxNativeZoom: 19,
        maxZoom: 22,
        attribution: "&copy; OpenStreetMap contributors",
      });
    };

    try {
      tileLayerRef.current?.remove();
    } catch (e) {
      // 無視
    }

    const nextLayer = buildTileLayer(mapTileLayer);
    nextLayer.addTo(map);
    tileLayerRef.current = nextLayer;

    return () => {
      try {
        nextLayer.remove();
      } catch (e) {
        // 無視
      }
    };
  }, [mapTileLayer]);

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;

    const ro = new ResizeObserver(() => {
      mapRef.current?.invalidateSize({ pan: false });
    });
    ro.observe(container);

    return () => ro.disconnect();
  }, []);

  // 拠点表示
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!basePoint) return;

    const latlng: L.LatLngExpression = [
      basePoint.map_latitude,
      basePoint.map_longitude,
    ];
    const safeZoom = Math.max(0, Math.min(22, basePoint.map_zoom ?? 14));
    map.setView(latlng, safeZoom, { animate: false });
    baseMarkerRef.current?.setLatLng(latlng).bindPopup(`${baseName || "拠点"}`);
  }, [basePoint, baseName]);

  // 選択時刻の座標へ追従（ON のときのみ）
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!followPointer) return;

    const lat = pointerPoint?.gps?.lat;
    const lng = pointerPoint?.gps?.lng;
    if (lat == null || lng == null) return;

    const zoom = map.getZoom();
    map.setView([lat, lng], zoom, { animate: false });
  }, [followPointer, pointerPoint?.gps?.lat, pointerPoint?.gps?.lng]);

  // 停留所表示
  useEffect(() => {
    const map = mapRef.current;
    const courseLayer = courseLayerRef.current;
    if (!map || !courseLayer) return;

    courseLayer.clearLayers();

    const stopIcon = L.icon({
      iconUrl: markerIcon,
      iconRetinaUrl: markerIcon2x,
      shadowUrl: markerShadow,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });

    coursePoints.forEach((p) => {
      const lat = Number(p.map_latitude);
      const lng = Number(p.map_longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const label = p.base_stop_name ?? "";
      const m = L.marker([lat, lng], { icon: stopIcon });
      if (label) m.bindPopup(label, { autoClose: false });
      courseLayer.addLayer(m);
    });
  }, [coursePoints]);

  // 軌跡ポイントクリア
  useEffect(() => {
    trailLayerRef.current?.clearLayers();
    trailMarkersRef.current = [];
    trailPointKeysRef.current = [];
    vehicleMarkerRef.current?.setOpacity(0);
    heatLayerRef.current?.clearLayers();
    heatSelectionLayerRef.current?.clearLayers();
    heatRectsRef.current = [];
    selectedKeyRef.current = null;
    ignoreNextMapClickRef.current = false;
    onPickInfoChange?.(null);
  }, [vehicleId, isRealtime, startInput, endInput, realtimeWindowMinutes]);

  // 実測ビットレート(bps)を閾値に応じて色へ変換する。
  const pickColorForActualBps = (v: number) => {
    if (!Number.isFinite(v)) return "#1976d2";

    const lowT = mapActualBitrateColoring.thresholdLowKbps;
    const midT = mapActualBitrateColoring.thresholdMidKbps;
    const lowC = mapActualBitrateColoring.lowColor;
    const midC = mapActualBitrateColoring.midColor;
    const highC = mapActualBitrateColoring.highColor;

    const vKbps = v * 1000;
    if (!Number.isFinite(vKbps)) return "#1976d2";

    const hexToRgb = (
      hex: string,
    ): { r: number; g: number; b: number } | null => {
      const m = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(hex);
      if (!m) return null;
      const r = parseInt(m[1], 16);
      const g = parseInt(m[2], 16);
      const b = parseInt(m[3], 16);
      if (![r, g, b].every((n) => Number.isFinite(n))) return null;
      return { r, g, b };
    };

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const clamp01Local = (t: number) => Math.max(0, Math.min(1, t));
    const rgbToHex = (rgb: { r: number; g: number; b: number }) => {
      const toHex = (n: number) =>
        Math.max(0, Math.min(255, Math.round(n)))
          .toString(16)
          .padStart(2, "0");
      return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
    };

    const a = hexToRgb(lowC);
    const b = hexToRgb(midC);
    const c = hexToRgb(highC);
    if (!a || !b || !c) {
      // RGB変換に失敗した場合は3段階の閾値色でフォールバックする。
      if (vKbps <= lowT) return lowC;
      if (vKbps < midT) return midC;
      return highC;
    }

    if (!Number.isFinite(lowT) || !Number.isFinite(midT) || midT <= lowT) {
      if (vKbps <= lowT) return lowC;
      if (vKbps < midT) return midC;
      return highC;
    }

    if (vKbps <= lowT) return lowC;
    if (vKbps >= midT) return highC;

    // [low..mid]を正規化してlow→mid→highの3点グラデーションで補間する。
    const u = clamp01Local((vKbps - lowT) / (midT - lowT));
    if (u < 0.5) {
      const t = u * 2;
      return rgbToHex({
        r: lerp(a.r, b.r, t),
        g: lerp(a.g, b.g, t),
        b: lerp(a.b, b.b, t),
      });
    }
    const t = (u - 0.5) * 2;
    return rgbToHex({
      r: lerp(b.r, c.r, t),
      g: lerp(b.g, c.g, t),
      b: lerp(b.b, c.b, t),
    });
  };

  // 弱さスコア(0..1)を色へ変換する（良い=highColor、悪い=lowColor）。
  const pickColorForWeakness = (w: number) => {
    const lowC = mapActualBitrateColoring.lowColor;
    const midC = mapActualBitrateColoring.midColor;
    const highC = mapActualBitrateColoring.highColor;

    const hexToRgb = (
      hex: string,
    ): { r: number; g: number; b: number } | null => {
      const m = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(hex);
      if (!m) return null;
      const r = parseInt(m[1], 16);
      const g = parseInt(m[2], 16);
      const b = parseInt(m[3], 16);
      if (![r, g, b].every((n) => Number.isFinite(n))) return null;
      return { r, g, b };
    };

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const clamp01Local = (t: number) => Math.max(0, Math.min(1, t));
    const rgbToHex = (rgb: { r: number; g: number; b: number }) => {
      const toHex = (n: number) =>
        Math.max(0, Math.min(255, Math.round(n)))
          .toString(16)
          .padStart(2, "0");
      return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
    };

    const a = hexToRgb(highC);
    const b = hexToRgb(midC);
    const c = hexToRgb(lowC);
    if (!a || !b || !c) {
      const ww = clamp01Local(w);
      if (ww <= scoreColorMid) return midC;
      return lowC;
    }

    const ww = clamp01Local(w);
    if (ww <= 0) return highC;
    if (ww >= scoreColorLow) return lowC;

    if (ww <= scoreColorMid) {
      const denom = Math.max(1e-6, scoreColorMid);
      const t = clamp01Local(ww / denom);
      return rgbToHex({
        r: lerp(a.r, b.r, t),
        g: lerp(a.g, b.g, t),
        b: lerp(a.b, b.b, t),
      });
    }

    const denom = Math.max(1e-6, scoreColorLow - scoreColorMid);
    const t = clamp01Local((ww - scoreColorMid) / denom);
    return rgbToHex({
      r: lerp(b.r, c.r, t),
      g: lerp(b.g, c.g, t),
      b: lerp(b.b, c.b, t),
    });
  };

  const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

  // 複数点のweaknessから範囲スコア(0..1)を算出し、下位側（弱い点の多さ）を強めに反映する。
  const calcAreaWeaknessScore = (weaknessValues: number[]) => {
    const n = weaknessValues.length;
    if (!n) return 0;

    const vals = weaknessValues.map((w) => clamp01(w));
    const mean = vals.reduce((s, w) => s + w, 0) / n;

    const sortedDesc = [...vals].sort((a, b) => b - a);
    const tailRatio = 0.2;
    const k = Math.max(1, Math.floor(n * tailRatio));
    const tailMean = sortedDesc.slice(0, k).reduce((s, w) => s + w, 0) / k;

    // tail平均を重くして「非常に弱い点が複数ある」ケースを目立たせる。
    return clamp01(mean * 0.2 + tailMean * 0.8);
  };

  // 昇順配列からq分位点(0..1)を取得する。
  const percentile = (sortedAsc: number[], q: number): number | null => {
    if (!sortedAsc.length) return null;
    const qq = clamp01(q);
    const idx = Math.floor((sortedAsc.length - 1) * qq);
    const v = sortedAsc[idx];
    return Number.isFinite(v) ? v : null;
  };

  // 選択円内の点群から統計情報（min/max/平均/分位/弱さ等）を組み立てる。
  const buildPickInfoFromPoints = (
    center: L.LatLng,
    points: Array<{ pt: BpsLogPoint; tMs: number; actual: number | null }>,
  ): BpsLogMapPickInfo => {
    const sorted = [...points].sort((a, b) => {
      const ta = a.tMs;
      const tb = b.tMs;
      if (!Number.isFinite(ta) && !Number.isFinite(tb)) return 0;
      if (!Number.isFinite(ta)) return 1;
      if (!Number.isFinite(tb)) return -1;
      return tb - ta;
    });

    let min: number | null = null;
    const actualValuesAsc: number[] = [];
    let max: number | null = null;
    let sum = 0;
    let cnt = 0;

    const lowKbps = mapActualBitrateColoring.thresholdLowKbps;
    let lowCount = 0;
    const weaknessValues: number[] = [];

    for (const p of sorted) {
      if (p.actual !== null) {
        actualValuesAsc.push(p.actual);
        min = min === null ? p.actual : Math.min(min, p.actual);
        max = max === null ? p.actual : Math.max(max, p.actual);
        sum += p.actual;
        cnt++;

        const actualKbps = p.actual * 1000;
        if (Number.isFinite(lowKbps) && lowKbps > 0) {
          if (Number.isFinite(actualKbps) && actualKbps < lowKbps) lowCount++;
        }
      }
      weaknessValues.push(calcWeakness(p.actual));
    }

    const avg = cnt ? sum / cnt : null;
    actualValuesAsc.sort((a, b) => a - b);
    const p10 = percentile(actualValuesAsc, 0.1);
    const weaknessScore = calcAreaWeaknessScore(weaknessValues);
    const lowRate = sorted.length ? lowCount / sorted.length : 0;
    const pickedPoint = sorted.length ? sorted[0].pt : null;

    return {
      centerLat: center.lat,
      centerLng: center.lng,
      count: sorted.length,
      pickedPoint,
      points: sorted.map((p) => p.pt),
      stats: {
        minActualMbps: min,
        p10ActualMbps: p10,
        maxActualMbps: max,
        avgActualMbps: avg,
        lowCount,
        lowRate,
        weaknessScore,
      },
    };
  };

  // 1点のactualから弱さスコア(0=良い..1=悪い)を計算する。
  const calcWeakness = (actualMbps: number | null) => {
    if (actualMbps === null) return 0;
    const lowKbps = mapActualBitrateColoring.thresholdLowKbps;
    const midKbps = mapActualBitrateColoring.thresholdMidKbps;
    if (!Number.isFinite(lowKbps) || lowKbps <= 0) return 0;
    if (!Number.isFinite(midKbps) || midKbps <= lowKbps) {
      const actualKbps = actualMbps * 1000;
      if (!Number.isFinite(actualKbps)) return 0;
      if (actualKbps >= lowKbps) return 0;
      return clamp01((lowKbps - actualKbps) / lowKbps);
    }

    const actualKbps = actualMbps * 1000;
    if (!Number.isFinite(actualKbps)) return 0;
    if (actualKbps >= midKbps) return 0;
    if (actualKbps >= lowKbps) {
      const t = (midKbps - actualKbps) / (midKbps - lowKbps);
      return clamp01(t) * 0.5;
    }
    const belowLow = clamp01((lowKbps - actualKbps) / lowKbps);
    return 0.5 + 0.5 * belowLow;
  };

  const getTrailPointKey = (pt: BpsLogPoint) => {
    const lat = pt?.gps?.lat;
    const lng = pt?.gps?.lng;
    const modemId = pt?.modem_id ?? "null";
    return `${modemId}:${pt.timestamp}:${lat ?? "null"}:${lng ?? "null"}`;
  };

  // 全ポイント表示（軌跡）
  useEffect(() => {
    if (mode !== "points") return;

    const trailLayer = trailLayerRef.current;
    if (!trailLayer) return;

    const renderSeq = ++renderBusySeqRef.current;
    markRenderBusy(true);

    const styleKey = [
      mapActualBitrateColoring.thresholdLowKbps,
      mapActualBitrateColoring.thresholdMidKbps,
      mapActualBitrateColoring.lowColor,
      mapActualBitrateColoring.midColor,
      mapActualBitrateColoring.highColor,
    ].join("|");
    const previousStyleKey = (trailLayer as any).__bpslogTrailStyleKey ?? null;
    const styleChanged = previousStyleKey !== styleKey;

    const nextTrailPoints = bpslogData.filter(hasGpsPoint);
    const nextTrailKeys = nextTrailPoints.map(getTrailPointKey);

    const clearTrail = () => {
      trailLayer.clearLayers();
      trailMarkersRef.current = [];
      trailPointKeysRef.current = [];
    };

    const startCancelToken = renderCancelTokenRef.current;
    if (
      !nextTrailPoints.length ||
      renderCancelTokenRef.current !== startCancelToken
    ) {
      clearTrail();
      (trailLayer as any).__bpslogTrailStyleKey = styleKey;
      requestAnimationFrame(() => {
        if (renderBusySeqRef.current === renderSeq) markRenderBusy(false);
      });
      return;
    }

    let startIdx = 0;
    if (!styleChanged && isRealtime && trailPointKeysRef.current.length > 0) {
      const currentKeys = trailPointKeysRef.current;
      let overlap = 0;
      const maxOverlap = Math.min(currentKeys.length, nextTrailKeys.length);
      for (let candidate = maxOverlap; candidate > 0; candidate--) {
        let matched = true;
        const currentStart = currentKeys.length - candidate;
        for (let i = 0; i < candidate; i++) {
          if (currentKeys[currentStart + i] !== nextTrailKeys[i]) {
            matched = false;
            break;
          }
        }
        if (matched) {
          overlap = candidate;
          break;
        }
      }

      if (overlap > 0 || nextTrailKeys[0] === currentKeys[0]) {
        const removeCount = currentKeys.length - overlap;
        if (removeCount > 0) {
          const removed = trailMarkersRef.current.splice(0, removeCount);
          removed.forEach((marker) => marker.remove());
          trailPointKeysRef.current.splice(0, removeCount);
        }
        startIdx = overlap;
      } else {
        clearTrail();
      }
    } else {
      clearTrail();
    }

    let rafId = 0;
    let idx = startIdx;
    const chunkSize = 1500;

    const step = () => {
      if (renderBusySeqRef.current !== renderSeq) return;
      if (renderCancelTokenRef.current !== startCancelToken) {
        markRenderBusy(false);
        return;
      }

      const end = Math.min(nextTrailPoints.length, idx + chunkSize);
      for (; idx < end; idx++) {
        if (renderCancelTokenRef.current !== startCancelToken) break;
        const pt = nextTrailPoints[idx];
        const color = pickColorForActualBps(pt.actual_bitrate ?? Number.NaN);
        const dot = L.circleMarker([pt.gps.lat, pt.gps.lng], {
          radius: 3,
          color,
          weight: 1,
          fillColor: color,
          fillOpacity: 0.85,
          interactive: false,
          renderer: trailRendererRef.current ?? undefined,
        });
        dot.addTo(trailLayer);
        trailMarkersRef.current.push(dot);
        trailPointKeysRef.current.push(nextTrailKeys[idx]);
      }

      if (
        idx < nextTrailPoints.length &&
        renderCancelTokenRef.current === startCancelToken
      ) {
        rafId = requestAnimationFrame(step);
        return;
      }

      (trailLayer as any).__bpslogTrailStyleKey = styleKey;

      requestAnimationFrame(() => {
        if (renderBusySeqRef.current === renderSeq) markRenderBusy(false);
      });
    };

    rafId = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [
    mode,
    bpslogData,
    mapActualBitrateColoring.thresholdLowKbps,
    mapActualBitrateColoring.thresholdMidKbps,
    mapActualBitrateColoring.lowColor,
    mapActualBitrateColoring.midColor,
    mapActualBitrateColoring.highColor,
  ]);

  // ヒートマップ（グリッド集計 / スムーズ）
  useEffect(() => {
    if (mode !== "heatmap") return;

    const heatLayer = heatLayerRef.current;
    if (!heatLayer) return;

    const selectionLayer = heatSelectionLayerRef.current;
    const map = mapRef.current;
    if (!map) return;

    const renderSeq = ++renderBusySeqRef.current;
    markRenderBusy(true);

    heatLayer.clearLayers();
    heatRectsRef.current = [];
    selectionLayer?.clearLayers();
    selectedKeyRef.current = null;
    onPickInfoChange?.(null);

    const startCancelToken = renderCancelTokenRef.current;
    if (
      !bpslogData.length ||
      renderCancelTokenRef.current !== startCancelToken
    ) {
      requestAnimationFrame(() => {
        if (renderBusySeqRef.current === renderSeq) markRenderBusy(false);
      });
      return;
    }

    let rafId = 0;
    let cleanupMapClick: (() => void) | null = null;
    const chunkSize = 1200;

    const finish = () => {
      requestAnimationFrame(() => {
        if (renderBusySeqRef.current === renderSeq) markRenderBusy(false);
      });
    };

    if (renderMode === "smooth") {
      const renderer = trailRendererRef.current ?? undefined;

      const heatPoints: Array<[number, number, number]> = [];
      const heatBucketSums = new Map<string, number>();
      const tinyPoints: Array<{
        lat: number;
        lng: number;
        actual: number | null;
      }> = [];
      const candidates: Array<{
        lat: number;
        lng: number;
        actual: number | null;
        tMs: number;
        pt: BpsLogPoint;
      }> = [];

      let idx = 0;

      const buildInputsStep = () => {
        if (renderBusySeqRef.current !== renderSeq) return;
        if (renderCancelTokenRef.current !== startCancelToken) {
          finish();
          return;
        }

        const end = Math.min(bpslogData.length, idx + chunkSize);
        for (; idx < end; idx++) {
          if (renderCancelTokenRef.current !== startCancelToken) break;
          const pt = bpslogData[idx];
          const lat = pt?.gps?.lat;
          const lng = pt?.gps?.lng;
          if (lat == null || lng == null) continue;

          const actual =
            typeof pt.actual_bitrate === "number" &&
            Number.isFinite(pt.actual_bitrate)
              ? pt.actual_bitrate
              : null;

          const w = calcWeakness(actual);
          if (w > 0) {
            heatPoints.push([lat, lng, w]);

            const projected = L.CRS.EPSG3857.project(L.latLng(lat, lng));
            const ix = Math.floor(projected.x / cellMeters);
            const iy = Math.floor(projected.y / cellMeters);
            const key = `${ix},${iy}`;
            heatBucketSums.set(key, (heatBucketSums.get(key) ?? 0) + w);
          }

          tinyPoints.push({ lat, lng, actual });

          const tMs = new Date(pt.timestamp).getTime();
          candidates.push({
            lat,
            lng,
            actual,
            tMs: Number.isFinite(tMs) ? tMs : Number.NaN,
            pt,
          });
        }

        if (
          idx < bpslogData.length &&
          renderCancelTokenRef.current === startCancelToken
        ) {
          rafId = requestAnimationFrame(buildInputsStep);
          return;
        }

        // heat layer
        if (renderCancelTokenRef.current !== startCancelToken) {
          finish();
          return;
        }

        if (heatPoints.length) {
          const sumsAsc = [...heatBucketSums.values()].sort((a, b) => a - b);
          const p95 = sumsAsc.length
            ? sumsAsc[Math.floor((sumsAsc.length - 1) * 0.95)]
            : 1;
          const heatMax = Math.max(1, Math.min(50, Number(p95) || 1));

          const midStop = Math.max(0, Math.min(1, scoreColorMid));
          const lowStop = Math.max(midStop, Math.min(1, scoreColorLow));
          const hiStop = Math.max(0.001, Math.min(midStop * 0.5, 0.08));

          const gradient: Record<number, string> = {
            0.0: "rgba(0, 0, 0, 0)",
            [hiStop]: mapActualBitrateColoring.highColor,
            [midStop]: mapActualBitrateColoring.midColor,
            [lowStop]: mapActualBitrateColoring.lowColor,
            1.0: mapActualBitrateColoring.lowColor,
          };

          const heat = L.heatLayer(heatPoints as any, {
            radius: 25,
            blur: 18,
            maxZoom: 19,
            max: heatMax,
            minOpacity: baseOpacity,
            gradient,
          });
          heat.addTo(heatLayer);
        }

        // tiny points
        let tinyIdx = 0;
        const drawTinyStep = () => {
          if (renderBusySeqRef.current !== renderSeq) return;
          if (renderCancelTokenRef.current !== startCancelToken) {
            finish();
            return;
          }
          const endTiny = Math.min(tinyPoints.length, tinyIdx + chunkSize);
          for (; tinyIdx < endTiny; tinyIdx++) {
            if (renderCancelTokenRef.current !== startCancelToken) break;
            const p = tinyPoints[tinyIdx];
            const color =
              p.actual === null
                ? "#1976d2"
                : pickColorForWeakness(calcWeakness(p.actual));
            const dot = L.circleMarker([p.lat, p.lng], {
              radius: 1.2,
              stroke: false,
              fill: true,
              fillColor: color,
              fillOpacity: 0.85,
              interactive: false,
              renderer,
            });
            dot.addTo(heatLayer);
          }

          if (
            tinyIdx < tinyPoints.length &&
            renderCancelTokenRef.current === startCancelToken
          ) {
            rafId = requestAnimationFrame(drawTinyStep);
            return;
          }

          // selection
          const computeSelectionRadiusMeters = (center: L.LatLng) => {
            const size = map.getSize();
            const minDim = Math.max(1, Math.min(size.x, size.y));

            // 半径はpx基準で決め、画面サイズに追従しつつズームアウト時は実距離(m)が自動的に大きくなる。
            const baseRadiusPx = Math.max(
              12,
              Math.min(60, Math.round(minDim * 0.04)),
            );

            // 高ズーム(>19)でも選択円が小さく見えるよう、px半径を追加で縮める。
            const zoom = map.getZoom();
            const overZoom = Math.max(0, zoom - 19);
            const shrinkFactor = Math.pow(0.9, overZoom);
            const radiusPx = Math.max(
              1,
              Math.round(baseRadiusPx * shrinkFactor),
            );

            const cpt = map.latLngToContainerPoint(center);
            const ept = L.point(cpt.x + radiusPx, cpt.y);
            const edge = map.containerPointToLatLng(ept);

            const meters = map.distance(center, edge);
            // 極端な値を避けつつ、縮小も許容する（高ズーム時にcellMetersで下限固定されないようにする）。
            return Math.max(5, Math.min(5000, meters));
          };

          const clearSelection = () => {
            selectedKeyRef.current = null;
            selectionLayer?.clearLayers();
            onPickInfoChange?.(null);
          };

          const drawSelectionCircle = (center: L.LatLng) => {
            if (!selectionLayer) return;
            selectionLayer.clearLayers();
            const radiusMeters = computeSelectionRadiusMeters(center);
            const circle = L.circle(center, {
              radius: radiusMeters,
              stroke: true,
              color: "#2b7df7",
              weight: 2,
              opacity: 1,
              fill: false,
              interactive: false,
              renderer,
            });
            circle.addTo(selectionLayer);
          };

          const onMapClick = (e: L.LeafletMouseEvent) => {
            const center = e.latlng;
            if (!center) {
              clearSelection();
              return;
            }

            const radiusMeters = computeSelectionRadiusMeters(center);

            // 簡易な矩形プリフィルタで距離計算を減らす。
            const latDeg = radiusMeters / 111_320;
            const lngDeg =
              radiusMeters /
              (111_320 * Math.max(0.1, Math.cos((center.lat * Math.PI) / 180)));

            const picked: Array<{
              pt: BpsLogPoint;
              tMs: number;
              actual: number | null;
            }> = [];
            for (const c of candidates) {
              if (Math.abs(c.lat - center.lat) > latDeg) continue;
              if (Math.abs(c.lng - center.lng) > lngDeg) continue;
              const d = map.distance(center, L.latLng(c.lat, c.lng));
              if (d <= radiusMeters) {
                picked.push({ pt: c.pt, tMs: c.tMs, actual: c.actual });
              }
            }

            if (!picked.length) {
              clearSelection();
              return;
            }

            selectedKeyRef.current = `smooth:${center.lat.toFixed(6)},${center.lng.toFixed(6)}`;
            drawSelectionCircle(center);
            onPickInfoChange?.(buildPickInfoFromPoints(center, picked));
          };

          map.on("click", onMapClick);
          cleanupMapClick = () => map.off("click", onMapClick);
          finish();
        };

        rafId = requestAnimationFrame(drawTinyStep);
      };

      rafId = requestAnimationFrame(buildInputsStep);
    } else {
      type Bucket = {
        ix: number;
        iy: number;
        points: Array<{ pt: BpsLogPoint; tMs: number; actual: number | null }>;
      };

      const buckets = new Map<string, Bucket>();
      let idx = 0;

      const buildBucketsStep = () => {
        if (renderBusySeqRef.current !== renderSeq) return;
        if (renderCancelTokenRef.current !== startCancelToken) {
          finish();
          return;
        }

        const end = Math.min(bpslogData.length, idx + chunkSize);
        for (; idx < end; idx++) {
          if (renderCancelTokenRef.current !== startCancelToken) break;
          const pt = bpslogData[idx];
          const lat = pt?.gps?.lat;
          const lng = pt?.gps?.lng;
          if (lat == null || lng == null) continue;

          const projected = L.CRS.EPSG3857.project(L.latLng(lat, lng));
          const ix = Math.floor(projected.x / cellMeters);
          const iy = Math.floor(projected.y / cellMeters);
          const key = `${ix},${iy}`;

          const bucket = buckets.get(key) ?? { ix, iy, points: [] };

          const tMs = new Date(pt.timestamp).getTime();
          const safeTMs = Number.isFinite(tMs) ? tMs : Number.NaN;
          const actual =
            typeof pt.actual_bitrate === "number" &&
            Number.isFinite(pt.actual_bitrate)
              ? pt.actual_bitrate
              : null;

          bucket.points.push({ pt, tMs: safeTMs, actual });
          buckets.set(key, bucket);
        }

        if (
          idx < bpslogData.length &&
          renderCancelTokenRef.current === startCancelToken
        ) {
          rafId = requestAnimationFrame(buildBucketsStep);
          return;
        }

        if (
          !buckets.size ||
          renderCancelTokenRef.current !== startCancelToken
        ) {
          finish();
          return;
        }

        const renderer = trailRendererRef.current ?? undefined;

        const buildPickInfo = (bucket: Bucket): BpsLogMapPickInfo => {
          const x0 = bucket.ix * cellMeters;
          const y0 = bucket.iy * cellMeters;
          const x1 = (bucket.ix + 1) * cellMeters;
          const y1 = (bucket.iy + 1) * cellMeters;

          const ll0 = L.CRS.EPSG3857.unproject(L.point(x0, y0));
          const ll1 = L.CRS.EPSG3857.unproject(L.point(x1, y1));
          const centerLat = (ll0.lat + ll1.lat) / 2;
          const centerLng = (ll0.lng + ll1.lng) / 2;

          const sorted = [...bucket.points].sort((a, b) => {
            const ta = a.tMs;
            const tb = b.tMs;
            if (!Number.isFinite(ta) && !Number.isFinite(tb)) return 0;
            if (!Number.isFinite(ta)) return 1;
            if (!Number.isFinite(tb)) return -1;
            return tb - ta;
          });

          let min: number | null = null;
          const actualValuesAsc: number[] = [];
          let max: number | null = null;
          let sum = 0;
          let cnt = 0;

          const lowKbps = mapActualBitrateColoring.thresholdLowKbps;
          let lowCount = 0;
          const weaknessValues: number[] = [];

          for (const p of sorted) {
            if (p.actual !== null) {
              actualValuesAsc.push(p.actual);
              min = min === null ? p.actual : Math.min(min, p.actual);
              max = max === null ? p.actual : Math.max(max, p.actual);
              sum += p.actual;
              cnt++;

              const actualKbps = p.actual * 1000;
              if (Number.isFinite(lowKbps) && lowKbps > 0) {
                if (Number.isFinite(actualKbps) && actualKbps < lowKbps)
                  lowCount++;
              }
            }

            weaknessValues.push(calcWeakness(p.actual));
          }

          const avg = cnt ? sum / cnt : null;
          const pickedPoint = sorted.length ? sorted[0].pt : null;
          const weaknessScore = calcAreaWeaknessScore(weaknessValues);
          const lowRate = sorted.length ? lowCount / sorted.length : 0;

          actualValuesAsc.sort((a, b) => a - b);
          const p10 = percentile(actualValuesAsc, 0.1);

          return {
            centerLat,
            centerLng,
            count: bucket.points.length,
            pickedPoint,
            points: sorted.map((p) => p.pt),
            stats: {
              minActualMbps: min,
              p10ActualMbps: p10,
              maxActualMbps: max,
              avgActualMbps: avg,
              lowCount,
              lowRate,
              weaknessScore,
            },
          };
        };

        const clearSelection = () => {
          selectedKeyRef.current = null;
          selectionLayer?.clearLayers();
          onPickInfoChange?.(null);
        };

        const drawSelectionOutline = (bounds: L.LatLngBounds) => {
          if (!selectionLayer) return;
          selectionLayer.clearLayers();
          const outline = L.rectangle(bounds, {
            stroke: true,
            color: "#2b7df7",
            weight: 2,
            opacity: 1,
            fill: false,
            interactive: false,
            renderer,
          });
          outline.addTo(selectionLayer);
        };

        const onMapClick = () => {
          if (ignoreNextMapClickRef.current) {
            ignoreNextMapClickRef.current = false;
            return;
          }
          clearSelection();
        };
        map.on("click", onMapClick);
        cleanupMapClick = () => map.off("click", onMapClick);

        const bucketsList = [...buckets.values()];
        let bidx = 0;
        const drawRectsStep = () => {
          if (renderBusySeqRef.current !== renderSeq) return;
          if (renderCancelTokenRef.current !== startCancelToken) {
            finish();
            return;
          }

          const endB = Math.min(bucketsList.length, bidx + chunkSize);
          for (; bidx < endB; bidx++) {
            if (renderCancelTokenRef.current !== startCancelToken) break;
            const bucket = bucketsList[bidx];
            const count = bucket.points.length;
            if (!count) continue;
            const weaknessValues: number[] = [];
            for (const p of bucket.points) {
              weaknessValues.push(calcWeakness(p.actual));
            }

            // tail平均で「非常に弱い点が複数ある」ケースを強調する。
            const weaknessScore = calcAreaWeaknessScore(weaknessValues);

            const key = `${bucket.ix},${bucket.iy}`;
            const x0 = bucket.ix * cellMeters;
            const y0 = bucket.iy * cellMeters;
            const x1 = (bucket.ix + 1) * cellMeters;
            const y1 = (bucket.iy + 1) * cellMeters;
            const ll0 = L.CRS.EPSG3857.unproject(L.point(x0, y0));
            const ll1 = L.CRS.EPSG3857.unproject(L.point(x1, y1));
            const bounds = L.latLngBounds(
              L.latLng(ll0.lat, ll0.lng),
              L.latLng(ll1.lat, ll1.lng),
            );

            // 色と不透明度は同じweaknessScoreから決める。
            const color = pickColorForWeakness(weaknessScore);

            // 不透明度はweaknessScoreに対して線形に割り当てる。
            const fillOpacity = Math.max(
              0,
              Math.min(
                1,
                baseOpacity + (1 - baseOpacity) * clamp01(weaknessScore),
              ),
            );

            const rect = L.rectangle(bounds, {
              stroke: false,
              fill: true,
              fillColor: color,
              fillOpacity,
              interactive: true,
              renderer,
            });

            rect.on("click", () => {
              ignoreNextMapClickRef.current = true;
              selectedKeyRef.current = key;
              drawSelectionOutline(bounds);
              onPickInfoChange?.(buildPickInfo(bucket));
            });

            rect.addTo(heatLayer);
            heatRectsRef.current.push(rect);
          }

          if (
            bidx < bucketsList.length &&
            renderCancelTokenRef.current === startCancelToken
          ) {
            rafId = requestAnimationFrame(drawRectsStep);
            return;
          }

          finish();
        };

        rafId = requestAnimationFrame(drawRectsStep);
      };

      rafId = requestAnimationFrame(buildBucketsStep);
    }

    return () => {
      cancelAnimationFrame(rafId);
      cleanupMapClick?.();
    };
  }, [
    mode,
    renderMode,
    bpslogData,
    cellMeters,
    baseOpacity,
    scoreColorMid,
    scoreColorLow,
    onPickInfoChange,
    onRenderBusyChange,
    renderCancelToken,
    mapActualBitrateColoring.thresholdLowKbps,
    mapActualBitrateColoring.thresholdMidKbps,
    mapActualBitrateColoring.lowColor,
    mapActualBitrateColoring.midColor,
    mapActualBitrateColoring.highColor,
  ]);

  // ポインタ（選択時刻）位置の反映
  useEffect(() => {
    const marker = vehicleMarkerRef.current;
    if (!marker) return;

    if (pointerPoint?.gps?.lat != null && pointerPoint?.gps?.lng != null) {
      marker.setLatLng([pointerPoint.gps.lat, pointerPoint.gps.lng]);
      marker.setOpacity(1);
    } else {
      marker.setOpacity(0);
    }
  }, [pointerPoint?.gps?.lat, pointerPoint?.gps?.lng]);

  return <div className="bpslogMap" ref={mapContainerRef} />;
};

export default BpsLogMap;
